import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { UsuarioDto } from '@models/usuario/usuarioDto.model';
import { LoginService } from '@services/login/login.service';
import { PermisoService } from '@services/permiso/permiso.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

type EvaluacionPermisos = 'AND' | 'OR';

@Directive({
  standalone: true,
  selector: '[appRoles]'
})
export class AppRolesDirective implements OnInit, OnDestroy {
  private requiredPermissions: string[] = [];
  private modo: EvaluacionPermisos = 'OR';
  private subscription?: Subscription;

  @Input()
  set appRoles(permisos: string[] | string) {
    // Manejar tanto string como array
    if (typeof permisos === 'string') {
      this.requiredPermissions = [permisos];
    } else {
      this.requiredPermissions = permisos || [];
    }
    this.evaluatePermissions();
  }

  @Input('appRolesModo')
  set appRolesModo(modo: EvaluacionPermisos) {
    this.modo = modo?.toUpperCase() === 'AND' ? 'AND' : 'OR';
    this.evaluatePermissions();
  }

  constructor(
    private loginService: LoginService,
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef,
    private permisoService: PermisoService
  ) {}

  ngOnInit(): void {
    // Cargar permisos del cache si no están disponibles
    this.ensurePermisosLoaded();

    // Suscribirse a cambios en permisos
    this.subscription = this.permisoService.permisosObservable
      .pipe(
        filter(permisos => permisos !== null) // Solo proceder cuando hay permisos
      )
      .subscribe(() => {
        this.evaluatePermissions();
      });

    // Evaluación inicial
    this.evaluatePermissions();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private ensurePermisosLoaded(): void {
    const user = this.loginService.getCurrentUser();
    if (!user?.rol?.id) return;

    // Intentar obtener permisos del cache usando el método público
    const cachedPermisos = this.permisoService.getPermisosCache(user.rol.id);

    // Si no hay permisos en cache, cargar desde el servicio
    if (!cachedPermisos) {
      this.permisoService.getPermisosPorRol(user.rol.id).subscribe({
        next: (permisos) => {
          console.log('Permisos cargados para la directiva');
        },
        error: (error) => {
          console.error('Error cargando permisos en directiva:', error);
        }
      });
    }
  }

  private evaluatePermissions(): void {
    this.viewContainerRef.clear();

    const user = this.loginService.getCurrentUser();
    if (!user?.rol?.id || !this.requiredPermissions.length) {
      return;
    }

    // Usar el método público del servicio
    const hasPermission = this.permisoService.hasPermissions(
      this.requiredPermissions,
      this.modo
    );

    if (hasPermission) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    }
  }
}
